import { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { CUTOFF_END_YEAR } from '../constants/years';
import { SITE_CONFIG } from '../config/siteConfig';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { FaSearch, FaMapMarkerAlt, FaUniversity, FaFilter, FaTimes, FaChevronLeft, FaChevronRight, FaHeart } from 'react-icons/fa';
import collegeApi, { CollegeSearchParams } from '@/api/colleges';
import axios from '@/api/axios';

const CollegeSearch = () => {
  const [filters, setFilters] = useState<CollegeSearchParams>({ page: 1, limit: 20, search: '' });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const { data: districtsData } = useQuery({
    queryKey: ['districts'],
    queryFn: async () => {
      const response = await axios.get('/districts');
      return response.data.districts || [];
    },
  });

  const { data: branchesData } = useQuery({
    queryKey: ['branches'],
    queryFn: async () => {
      const response: any = await axios.get('/courses/branches/distinct');
      return response.branches || [];
    },
  });

  const districts = districtsData || [];
  const branches = branchesData || [];

  const { data, isLoading, error } = useQuery({
    queryKey: ['colleges', filters],
    queryFn: () => collegeApi.getAll(filters),
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFilterChange = (key: keyof CollegeSearchParams, value: any) => {
    const newFilters: CollegeSearchParams = { ...filters, page: 1 };
    if (value === undefined || value === null || value === '') {
      delete newFilters[key];
    } else {
      (newFilters as any)[key] = value;
    }
    setFilters(newFilters);
  };

  const hasActiveFilters =
    filters.search ||
    filters.district_id ||
    filters.college_type ||
    (filters as any).branch_code ||
    (filters as any).autonomous_status ||
    filters.naac_grade ||
    filters.boys_hostel ||
    filters.girls_hostel ||
    filters.transport;

  const clearFilters = () => setFilters({ page: 1, limit: 20, search: '' });

  const pagination = data?.data?.pagination;
  const colleges = data?.data?.colleges || [];
  const totalPages = pagination?.totalPages || 1;
  const currentPage = filters.page || 1;

  // Smart pagination: show first, last, and pages around current
  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '...')[] = [];
    pages.push(1);
    if (currentPage > 3) pages.push('...');
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      pages.push(i);
    }
    if (currentPage < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  };

  // Shorten college name — strip address after the first comma if name is too long
  const shortenName = (name: string) => {
    // Preserve Anna University campus identifier (CEG/MIT/ACT)
    const campusMatch = name.match(/^(.*?)\s*[-–]\s*([A-Z]{2,5}\s*Campus)/i);
    if (campusMatch) {
      const base = campusMatch[1].split(',')[0].trim();
      return `${base} — ${campusMatch[2]}`;
    }
    if (name.length <= 60) return name;
    const commaIdx = name.indexOf(',');
    if (commaIdx > 20) return name.substring(0, commaIdx);
    return name.substring(0, 60) + '…';
  };

  return (
    <>
      <Helmet>
        <title>{`Tamil Nadu Engineering Colleges ${SITE_CONFIG.year} — Search 476+ TNEA Colleges by District, Branch & NAAC | ChooseMyCollege`}</title>
        <meta name="description" content={`Browse and filter 476+ engineering colleges in Tamil Nadu for TNEA ${SITE_CONFIG.year}. Filter by district, college type (Govt/Aided/Private), NAAC grade, hostel availability, branch and fees.`} />
        <meta name="keywords" content={`engineering colleges Tamil Nadu ${SITE_CONFIG.year}, TNEA colleges list, government engineering colleges Tamil Nadu, Anna University affiliated colleges, NAAC accredited colleges Tamil Nadu`} />
        <meta property="og:title" content={`Tamil Nadu Engineering Colleges ${SITE_CONFIG.year} — Search 476+ TNEA Colleges | ChooseMyCollege`} />
        <meta property="og:description" content={`Browse 476+ engineering colleges in Tamil Nadu for TNEA ${SITE_CONFIG.year}. Filter by district, NAAC grade, hostel, branch and fees.`} />
        <meta property="og:url" content="https://choosemycollege.in/colleges" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://choosemycollege.in/colleges" />
      </Helmet>

      <div className="w-full max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Engineering Colleges</h1>
          <p className="text-sm text-gray-500 mt-1">420+ colleges across 38 districts in Tamil Nadu</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-5 mb-6">
          {/* Search */}
          <div className="relative mb-4" ref={searchRef}>
            <FaSearch className="absolute left-3 top-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by college name or code…"
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={filters.search}
              onChange={(e) => {
                setFilters({ ...filters, search: e.target.value, page: 1 });
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
            />
            {filters.search && (
              <button
                className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
                onClick={() => setFilters({ ...filters, search: '', page: 1 })}
              >
                <FaTimes />
              </button>
            )}
            {/* Autocomplete */}
            {showSuggestions && filters.search && colleges.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-72 overflow-y-auto">
                {colleges.slice(0, 8).map((college: any) => (
                  <Link
                    key={college.id}
                    to={`/colleges/${college.college_code}`}
                    onClick={() => setShowSuggestions(false)}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 border-b border-gray-50 last:border-0 transition-colors"
                  >
                    <FaUniversity className="text-blue-500 flex-shrink-0 text-sm" />
                    <div className="min-w-0">
                      <div className="font-medium text-gray-800 text-sm truncate">{shortenName(college.college_name)}</div>
                      <div className="text-xs text-gray-400">{college.college_code} · {college.district?.name || college.district_name}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Primary filters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <select
              className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              value={filters.district_id || ''}
              onChange={(e) => handleFilterChange('district_id', e.target.value ? parseInt(e.target.value) : undefined)}
            >
              <option value="">All Districts</option>
              {districts.map((d: any) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>

            <select
              className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              value={filters.college_type || ''}
              onChange={(e) => handleFilterChange('college_type', e.target.value || undefined)}
            >
              <option value="">All Types</option>
              <option value="Government">Government</option>
              <option value="Govt Aided">Govt. Aided</option>
              <option value="Anna Univ - Constituent">Anna Univ - Regional</option>
              <option value="Anna University">Anna University</option>
              <option value="Deemed University">Deemed University</option>
              <option value="Private">Private (Self Financing)</option>
            </select>

            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`flex items-center justify-center gap-2 px-3 py-2.5 border rounded-lg text-sm font-medium transition-colors ${
                showAdvancedFilters
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <FaFilter className="text-xs" /> More Filters
            </button>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center justify-center gap-2 px-3 py-2.5 border border-red-200 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors"
              >
                <FaTimes className="text-xs" /> Clear All
              </button>
            )}
          </div>

          {/* Advanced filters */}
          {showAdvancedFilters && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t border-gray-100">
              <select
                className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                value={(filters as any).branch_code || ''}
                onChange={(e) => handleFilterChange('branch_code' as any, e.target.value || undefined)}
              >
                <option value="">All Departments</option>
                {branches.filter((b: any) => !['Architecture', 'Design', 'Planning'].includes(b.category)).map((b: any) => <option key={b.branch_code} value={b.branch_code}>{b.display_name}</option>)}
              </select>

              <select
                className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                value={filters.naac_grade || ''}
                onChange={(e) => handleFilterChange('naac_grade', e.target.value || undefined)}
              >
                <option value="">Any NAAC Grade</option>
                <option value="A++">A++</option>
                <option value="A+">A+</option>
                <option value="A">A</option>
                <option value="B++">B++</option>
                <option value="B+">B+</option>
                <option value="B">B</option>
              </select>

              <select
                className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                value={(filters as any).autonomous_status || ''}
                onChange={(e) => handleFilterChange('autonomous_status' as any, e.target.value || undefined)}
              >
                <option value="">Autonomous (Any)</option>
                <option value="true">Autonomous Only</option>
                <option value="false">Non-Autonomous</option>
              </select>

              <select
                className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                value={filters.boys_hostel ? 'boys' : filters.girls_hostel ? 'girls' : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  const next = { ...filters, page: 1 };
                  delete next.boys_hostel;
                  delete next.girls_hostel;
                  if (v === 'boys') next.boys_hostel = true;
                  if (v === 'girls') next.girls_hostel = true;
                  setFilters(next);
                }}
              >
                <option value="">Hostel (Any)</option>
                <option value="boys">Boys Hostel</option>
                <option value="girls">Girls Hostel</option>
              </select>
            </div>
          )}
        </div>

        {/* Result count */}
        {!isLoading && pagination && (
          <p className="text-sm text-gray-500 mb-4">
            {pagination.total === 0
              ? 'No colleges found'
              : `Showing ${(currentPage - 1) * 20 + 1}–${Math.min(currentPage * 20, pagination.total)} of ${pagination.total} colleges`}
          </p>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4" />
            <p className="text-gray-500 text-sm">Loading colleges…</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            Failed to load colleges. Please refresh the page.
          </div>
        )}

        {/* College Cards */}
        {!isLoading && colleges.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {colleges.map((college: any) => (
              <div
                key={college.id}
                className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 hover:shadow-md hover:border-blue-200 transition-all duration-200 group flex flex-col"
              >
                <Link to={`/colleges/${college.college_code}`} className="flex-1">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition-colors">
                      <FaUniversity className="text-blue-600 text-sm" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-800 text-sm leading-snug line-clamp-2 group-hover:text-blue-700 transition-colors">
                        {shortenName(college.college_name)}
                      </h3>
                      <p className="text-xs text-gray-400 mt-0.5">Code: {college.college_code}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 text-xs text-gray-500 mb-3">
                    <FaMapMarkerAlt className="text-blue-400 flex-shrink-0" />
                    {college.district?.name || college.district_name}
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                      {college.college_type === 'Private' ? 'Self Financing' : college.college_type === 'Govt Aided' ? 'Govt. Aided' : college.college_type === 'Anna Univ - Constituent' ? 'Anna Univ - Regional' : college.college_type === 'Deemed University' ? 'Deemed University' : college.college_type}
                    </span>
                    {college.naac_grade && (
                      <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                        NAAC {college.naac_grade}
                      </span>
                    )}
                    {college.autonomous_status && (
                      <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">
                        Autonomous
                      </span>
                    )}
                    {college.boys_accommodation_available && (
                      <span className="px-2 py-0.5 bg-sky-50 text-sky-700 rounded-full text-xs font-medium">
                        🏠 Boys Hostel
                      </span>
                    )}
                    {college.girls_accommodation_available && (
                      <span className="px-2 py-0.5 bg-pink-50 text-pink-700 rounded-full text-xs font-medium">
                        🏠 Girls Hostel
                      </span>
                    )}
                  </div>
                </Link>

                {/* ChoiceList: open college page to pick a branch */}
                <Link
                  to={`/colleges/${college.college_code}`}
                  className="mt-3 w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-500 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <FaHeart className="text-[10px] text-red-500" />
                  Add to ChoiceList (pick branch)
                </Link>
              </div>
            ))}
          </div>
        )}

        {/* Smart Pagination */}
        {!isLoading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-1.5 pb-4">
            <button
              onClick={() => setFilters({ ...filters, page: currentPage - 1 })}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <FaChevronLeft className="text-xs" />
            </button>

            {getPageNumbers().map((page, i) =>
              page === '...' ? (
                <span key={`dots-${i}`} className="px-2 text-gray-400 text-sm">…</span>
              ) : (
                <button
                  key={page}
                  onClick={() => setFilters({ ...filters, page: page as number })}
                  className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                    page === currentPage
                      ? 'bg-blue-600 text-white'
                      : 'border border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              )
            )}

            <button
              onClick={() => setFilters({ ...filters, page: currentPage + 1 })}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <FaChevronRight className="text-xs" />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CollegeSearch;
